/**
 * Child Access Service
 * Manages care team access and permissions for child-specific features
 */

import { 
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { getE2EMockData, isE2EMockEnabled } from './e2eMock';
import { USER_ROLES, getRolePermissions } from '../constants/roles';

const TEAM_ROLE_KEY_MAP = [
  { role: USER_ROLES.CARE_OWNER, key: 'care_owner', multiple: false },
  { role: USER_ROLES.CARE_PARTNER, key: 'care_partners', multiple: true },
  { role: USER_ROLES.CAREGIVER, key: 'caregivers', multiple: true },
  { role: USER_ROLES.THERAPIST, key: 'therapists', multiple: true },
];

const getUserProfile = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) {
    console.warn(`User document not found for userId: ${userId}`);
    return null;
  }

  const userData = userDoc.data();
  return {
    userId,
    displayName: userData.displayName || userData.name || 'Unknown User',
    email: userData.email || '',
    photoURL: userData.photoURL || null,
    isOnline: userData.isOnline || false,
    lastSeen: userData.lastSeen || null,
  };
};

/**
 * Get care team members for a specific child
 * Returns users who have access to this child with their roles
 * 
 * @param {string} childId - Child ID to get care team for
 * @returns {Promise<Array>} Array of care team members with user info and roles
 */
export const getChildCareTeam = async (childId) => {
  if (isE2EMockEnabled()) {
    const mockData = getE2EMockData();
    return mockData.careTeamsByChildId?.[childId] || [];
  }

  try {
    if (!childId) {
      throw new Error('Child ID is required');
    }

    // Source of truth: children.users role arrays.
    // Messaging, care team, and role checks should resolve from the same child membership shape.
    const childDoc = await getDoc(doc(db, 'children', childId));
    if (!childDoc.exists()) {
      throw new Error(`Child ${childId} not found`);
    }

    const childData = childDoc.data();
    const users = childData.users || {};
    const careTeamMembers = [];

    for (const { role, key, multiple } of TEAM_ROLE_KEY_MAP) {
      const memberIds = multiple
        ? Array.isArray(users[key]) ? users[key] : []
        : users[key] ? [users[key]] : [];

      for (const userId of memberIds) {
        if (!userId) {
          continue;
        }

        try {
          const profile = await getUserProfile(userId);
          if (!profile) {
            continue;
          }

          careTeamMembers.push({
            ...profile,
            role,
            permissions: getRolePermissions(role),
          });
        } catch (error) {
          console.warn(`Error fetching user ${userId}:`, error);
        }
      }
    }

    console.log(`📋 Found ${careTeamMembers.length} care team members for child ${childId}`);
    return careTeamMembers;

  } catch (error) {
    if (error?.code === 'permission-denied') {
      console.warn(`Permission denied reading child care team for ${childId}; treating as no accessible team.`);
      return [];
    }

    console.error('Error getting child care team:', error);
    throw error;
  }
};

/**
 * Get all approved team members across all children for a user
 * Returns unique users who have access to any of the user's children
 * 
 * @param {Array} userChildIds - Array of child IDs the user has access to
 * @returns {Promise<Array>} Array of all team members across all children
 */
export const getAllApprovedTeamMembers = async (userChildIds) => {
  try {
    if (!userChildIds || userChildIds.length === 0) {
      return [];
    }

    const allTeamMembers = new Map(); // Use Map to avoid duplicates

    // Get care team for each child
    for (const childId of userChildIds) {
      try {
        const careTeam = await getChildCareTeam(childId);
        
        // Add each team member to the map (key = userId)
        careTeam.forEach(member => {
          if (!allTeamMembers.has(member.userId)) {
            // Add child context to track which children this member has access to
            allTeamMembers.set(member.userId, {
              ...member,
              childAccess: [{ childId, role: member.role }]
            });
          } else {
            // Add this child to existing member's access list
            const existing = allTeamMembers.get(member.userId);
            existing.childAccess.push({ childId, role: member.role });
          }
        });
      } catch (error) {
        console.warn(`Error getting care team for child ${childId}:`, error);
      }
    }

    const uniqueTeamMembers = Array.from(allTeamMembers.values());
    console.log(`👥 Found ${uniqueTeamMembers.length} unique team members across all children`);
    
    return uniqueTeamMembers;

  } catch (error) {
    console.error('Error getting all approved team members:', error);
    throw error;
  }
};

/**
 * Check if a user has access to message about a specific child
 * 
 * @param {string} userId - User ID to check
 * @param {string} childId - Child ID to check access for
 * @returns {Promise<boolean>} True if user has access
 */
export const canUserMessageAboutChild = async (userId, childId) => {
  try {
    if (!userId || !childId) {
      return false;
    }

    const childDoc = await getDoc(doc(db, 'children', childId));
    if (!childDoc.exists()) {
      return false;
    }

    const users = childDoc.data()?.users || {};
    const allowed = (
      users.care_owner === userId ||
      users.care_partners?.includes(userId) ||
      users.caregivers?.includes(userId) ||
      users.therapists?.includes(userId) ||
      false
    );

    return allowed;

  } catch (error) {
    if (error?.code === 'permission-denied') {
      return false;
    }

    console.error('Error checking child access:', error);
    return false;
  }
};
